import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { defaultLpsInfos, getInsIdentityResultMessage } from '@/constants/dmp-connect'
import { useLazyGetInsFromVitaleCardQuery } from '@/core/services/dmp-connect-js2'
import { InsIdentity, LpsInfos, VitaleCardPatient } from '@/core/types/dmp-connect'
import { InsOperation } from '@/core/models/ins-audit.model'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { replaceAlerts } from '@/redux/reducers/alerts.reducer'
import { selectDMPConnectTlsiConnectorBillingNumber, selectDMPConnectTlsiConnectorId } from '@/redux/reducers/dmp-connect.reducer'
import { getApiErrorMessage } from '@/utils/errors'
import { useLogInsCall } from '@/pages/patient/model/useLogInsCall'

import { getInsIdentityErrors, toReturnedIdentity } from '../lib/insIdentity'

export const DMP_CONNECT_VITALE_CARD_FORM_ALERTS = 'DMP_CONNECT_VITALE_CARD_FORM_ALERTS'

/** INSi found exactly one identity for the card holder. */
const UNIQUE_IDENTITY_FOUND = 2

type UseGetInsFromVitaleCardParams = {
  /** Position of the beneficiary on the Vitale card. */
  index: number
  patient: VitaleCardPatient
  /** MedConnect patient the INS call is logged against, when known. */
  patientId?: string
  onIdentity: (identity: InsIdentity) => void
}

/**
 * Calls INSi for one beneficiary of the inserted Vitale card, logs the call in the INS audit
 * trail, surfaces the outcome (alert / snackbars) and hands the usable identity to `onIdentity`.
 */
export function useGetInsFromVitaleCard({ index, patient, patientId, onIdentity }: UseGetInsFromVitaleCardParams) {
  const dispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const billingNumber = useAppSelector(selectDMPConnectTlsiConnectorBillingNumber)
  const tlsiConnectorId = useAppSelector(selectDMPConnectTlsiConnectorId)
  const logInsCall = useLogInsCall()
  const [getInsFromVitaleCard, { data, isLoading }] = useLazyGetInsFromVitaleCardQuery()

  const getIns = async () => {
    if (!tlsiConnectorId) return
    const patientFullName = `${patient.s_name} ${patient.s_given}`.trim() || undefined

    try {
      const LpsInfos: LpsInfos = { ...defaultLpsInfos, s_instance: tlsiConnectorId, s_billingNumber: billingNumber }
      const { i_insIdentityResult, Identity } = await getInsFromVitaleCard({ i_vitalePatientIndex: index, LpsInfos }).unwrap()

      const returnedIdentity = i_insIdentityResult === UNIQUE_IDENTITY_FOUND && Identity ? toReturnedIdentity(Identity) : undefined
      await logInsCall({
        patient: patientId,
        patientFullName,
        operation: InsOperation.GET_INS_WITH_CARD,
        resultCode: i_insIdentityResult,
        returnedIdentity,
        returnStatus: returnedIdentity ? 'OK' : undefined,
      })

      if (i_insIdentityResult !== UNIQUE_IDENTITY_FOUND) {
        dispatch(
          replaceAlerts({ area: DMP_CONNECT_VITALE_CARD_FORM_ALERTS, alerts: [{ message: getInsIdentityResultMessage(i_insIdentityResult), type: 'error' }] })
        )
      }
      if (Identity) {
        const errors = getInsIdentityErrors(Identity)
        if (errors.length > 0) {
          enqueueSnackbar(errors.join('\n'), { variant: 'error', style: { whiteSpace: 'pre-line' } })
          return
        }
        onIdentity(Identity)
      }
      enqueueSnackbar(t`The INS has been retrieved successfully`, { variant: 'success' })
    } catch (error) {
      await logInsCall({ patient: patientId, patientFullName, operation: InsOperation.GET_INS_WITH_CARD })
      enqueueSnackbar(getApiErrorMessage(error, t`Get INS failed due to unknown error`), { variant: 'error' })
    }
  }

  return { getIns, isLoading, hasResult: !!data, hasIdentity: !!data?.Identity }
}
